import React, { useEffect, useState } from "react";
import axios from "axios";
import { Cloud, Droplets, Wind, Thermometer } from "lucide-react";
import { API } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";

const WEATHER_CODE = {
  0: "Clear sky",
  1: "Mostly clear",
  2: "Partly cloudy",
  3: "Overcast",
  45: "Fog",
  48: "Rime fog",
  51: "Light drizzle",
  53: "Drizzle",
  55: "Heavy drizzle",
  61: "Light rain",
  63: "Rain",
  65: "Heavy rain",
  71: "Snow",
  80: "Rain showers",
  95: "Thunderstorm",
};

export const WeatherWidget = ({ lat = 18.52, lon = 73.86, language = "hi" }) => {
  const { t } = useLang();
  const [wx, setWx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coords, setCoords] = useState({ lat, lon });

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
        () => setCoords({ lat, lon }),
        { timeout: 5000 }
      );
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/weather`, { params: { lat: coords.lat, lon: coords.lon, language } })
      .then((r) => !cancelled && setWx(r.data))
      .catch(() => !cancelled && setWx(null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [coords, language]);

  return (
    <div
      data-testid="weather-widget"
      className="bg-gradient-to-br from-[#52796F] to-[#1B4332] text-white rounded-3xl p-6 shadow-md"
    >
      <div className="flex items-center justify-between">
        <h3 className="cd-heading text-lg font-bold flex items-center gap-2">
          <Cloud className="w-5 h-5" /> {t("dash.weather")}
        </h3>
        {wx && (
          <span className="text-xs text-[#D4A373]">
            {WEATHER_CODE[wx.current?.weather_code] || "—"}
          </span>
        )}
      </div>

      {loading && (
        <div className="py-6 text-sm text-white/70" data-testid="weather-loading">
          {t("dash.weatherLoading")}
        </div>
      )}

      {!loading && wx && (
        <>
          <div className="grid grid-cols-3 gap-3 mt-4">
            <Metric
              icon={<Thermometer className="w-4 h-4" />}
              label="Temp"
              value={`${Math.round(wx.current?.temperature_2m ?? 0)}°C`}
            />
            <Metric
              icon={<Droplets className="w-4 h-4" />}
              label="Humidity"
              value={`${wx.current?.relative_humidity_2m ?? 0}%`}
            />
            <Metric
              icon={<Wind className="w-4 h-4" />}
              label="Wind"
              value={`${Math.round(wx.current?.wind_speed_10m ?? 0)}km/h`}
            />
          </div>

          {wx.advice && (
            <div
              data-testid="weather-advice"
              className="mt-4 bg-white/10 rounded-2xl p-3 text-sm whitespace-pre-line"
            >
              {wx.advice}
            </div>
          )}
        </>
      )}

      {!loading && !wx && (
        <div className="py-4 text-sm text-white/70">{t("dash.weatherUnavail")}</div>
      )}
    </div>
  );
};

const Metric = ({ icon, label, value }) => (
  <div className="bg-white/10 rounded-2xl p-3 flex flex-col gap-1">
    <div className="flex items-center gap-1 text-[#D4A373] text-xs">
      {icon} {label}
    </div>
    <div className="text-lg font-bold">{value}</div>
  </div>
);
